/**
 * VIVA HUB CRM — lead detail
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import HubLayout from '../components/hub/HubLayout'
import CrmHelpLink from '../components/hub/crm/CrmHelpLink'
import CopyableField from '../components/hub/CopyableField'
import CrmLeadTimeline from '../components/hub/crm/CrmLeadTimeline'
import ConfirmDialog from '../components/scheduler/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { hubCrmAPI } from '../utils/hub/api'
import { formatDbDateTime } from '../utils/datetime'
import { flattenIntakeFields } from '../lib/intake-display'
import { flattenContactFields } from '../lib/contact-display'
import {
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
  PARTICIPANT_ROLE_LABELS,
  statusBadgeClass,
} from '../lib/crm-display'

function CollapsibleSection({ title, summary, children }) {
  return (
    <details className="bg-white rounded-lg border border-gray-200 group">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 [&::-webkit-details-marker]:hidden">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {summary ? <p className="text-xs text-gray-500 mt-0.5 truncate">{summary}</p> : null}
        </div>
        <svg
          className="size-5 shrink-0 text-gray-400 transition-transform group-open:rotate-180"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </summary>
      <div className="border-t border-gray-100 px-4 pb-4 pt-3">{children}</div>
    </details>
  )
}

function resolveClientEmail(lead, participants) {
  if (lead?.email?.trim()) return lead.email.trim()
  const list = participants || []
  const row =
    list.find((p) => p.is_primary_contact && p.email?.trim()) ||
    list.find((p) => p.role === 'contact' && p.email?.trim()) ||
    list.find((p) => p.email?.trim())
  return row?.email?.trim() || ''
}

function resolveClientPhone(lead, participants) {
  if (lead?.phone_normalized) {
    return lead.phone_raw?.trim() || lead.phone_normalized
  }
  const list = participants || []
  const row =
    list.find((p) => p.is_primary_contact && p.phone_normalized) ||
    list.find((p) => p.role === 'contact' && p.phone_normalized) ||
    list.find((p) => p.phone_normalized)
  return row?.phone_raw?.trim() || row?.phone_normalized || ''
}

function hasMarketingConsent(lead) {
  return lead?.marketing_consent !== 0
}

export default function HubCRMLead() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [spReference, setSpReference] = useState('')
  const [intakeLinkLang, setIntakeLinkLang] = useState('en')
  const [intakeLinkChannels, setIntakeLinkChannels] = useState(['email'])
  const [intakeLinkNotice, setIntakeLinkNotice] = useState('')
  const [sendingIntakeLink, setSendingIntakeLink] = useState(false)
  const [intakeLinkConfirmOpen, setIntakeLinkConfirmOpen] = useState(false)
  const [optOutConfirmOpen, setOptOutConfirmOpen] = useState(false)
  const [nudgeConfirmOpen, setNudgeConfirmOpen] = useState(false)
  const [nudgeRecipientId, setNudgeRecipientId] = useState('')
  const [nudgeNote, setNudgeNote] = useState('')
  const [sendingNudge, setSendingNudge] = useState(false)
  const [nudgeNotice, setNudgeNotice] = useState('')
  const [callingLead, setCallingLead] = useState(false)
  const [callNotice, setCallNotice] = useState('')

  const loadLead = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await hubCrmAPI.getLead(id)
      setData(result)
      setSpReference(result.lead?.sp_reference || '')
    } catch (err) {
      setError(err.message || 'Failed to load case')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadLead()
  }, [loadLead])

  const openIntakeLinkDialog = () => {
    if (!data?.lead) return
    const lead = data.lead
    const participants = data.participants || []
    const email = resolveClientEmail(lead, participants)
    const phone = resolveClientPhone(lead, participants)
    const smsOk =
      data.twilio_sms_configured && phone && hasMarketingConsent(lead)
    const defaults = []
    if (email) defaults.push('email')
    if (smsOk) defaults.push('sms')
    setIntakeLinkLang(lead.preferred_language === 'pt' ? 'pt' : 'en')
    setIntakeLinkChannels(defaults.length ? defaults : ['email'])
    setIntakeLinkConfirmOpen(true)
  }

  const openNudgeDialog = () => {
    const lead = data?.lead
    const recipients = (data?.nudge_recipients || []).filter((r) => r.can_receive_nudge)
    if (!lead || !recipients.length) return
    const defaultId =
      lead.assigned_to_user_id &&
      recipients.some((r) => r.id === lead.assigned_to_user_id)
        ? String(lead.assigned_to_user_id)
        : String(recipients[0].id)
    setNudgeRecipientId(defaultId)
    setNudgeNote('')
    setNudgeConfirmOpen(true)
  }

  const performSendNudge = async () => {
    const recipientId = parseInt(nudgeRecipientId, 10)
    if (!recipientId || Number.isNaN(recipientId)) return
    setNudgeConfirmOpen(false)
    setSendingNudge(true)
    setNudgeNotice('')
    setError('')
    try {
      const result = await hubCrmAPI.sendAssigneeNudge(id, {
        recipient_user_id: recipientId,
        note: nudgeNote.trim() || undefined,
      })
      setData(result)
      const name =
        result.nudge?.to_name ||
        result.nudge_recipients?.find((r) => r.id === recipientId)?.name
      setNudgeNotice(`Reminder email sent to ${name || 'team member'}.`)
    } catch (err) {
      setError(err.message || 'Failed to send reminder')
    } finally {
      setSendingNudge(false)
    }
  }

  const handleCallLead = async () => {
    setCallingLead(true)
    setCallNotice('')
    setError('')
    try {
      const result = await hubCrmAPI.callLead(id)
      setCallNotice(result.message || 'Calling your phone now…')
    } catch (err) {
      setError(err.message || 'Failed to start call')
    } finally {
      setCallingLead(false)
    }
  }

  const performSendIntakeLink = async () => {
    if (!intakeLinkChannels.length) return
    setIntakeLinkConfirmOpen(false)
    setSendingIntakeLink(true)
    setIntakeLinkNotice('')
    setError('')
    try {
      const result = await hubCrmAPI.sendIntakeLink(id, {
        language: intakeLinkLang,
        channels: intakeLinkChannels,
      })
      setData(result)
      const sent = result.sent
      const parts = (sent?.deliveries || []).map((d) => {
        const label = d.channel === 'sms' ? 'SMS' : 'Email'
        return `${label} to ${d.to}`
      })
      let notice = parts.length
        ? `Intake form link sent: ${parts.join('; ')}.`
        : `Intake form link sent to ${sent?.to || 'client'}.`
      if (sent?.errors?.length) {
        notice += ` Note: ${sent.errors.join('; ')}`
      }
      setIntakeLinkNotice(notice)
    } catch (err) {
      setError(err.message || 'Failed to send intake form link')
    } finally {
      setSendingIntakeLink(false)
    }
  }

  const toggleIntakeLinkChannel = (channelId, enabled) => {
    setIntakeLinkChannels((prev) => {
      const next = new Set(prev)
      if (enabled) next.add(channelId)
      else next.delete(channelId)
      return [...next]
    })
  }

  const handleStatusChange = async (status) => {
    setSaving(true)
    try {
      await hubCrmAPI.updateLead(id, { status })
      await loadLead()
    } catch (err) {
      alert(err.message || 'Failed to update status')
    } finally {
      setSaving(false)
    }
  }

  const performOptOutFollowUp = async () => {
    setOptOutConfirmOpen(false)
    setSaving(true)
    try {
      await hubCrmAPI.updateLead(id, {
        marketing_consent: false,
        marketing_consent_note: 'Opted out in CRM',
      })
      await loadLead()
    } catch (err) {
      alert(err.message || 'Failed to update')
    } finally {
      setSaving(false)
    }
  }

  const handleAssigneeChange = async (assignedTo) => {
    setSaving(true)
    try {
      const value = assignedTo === '' ? null : parseInt(assignedTo, 10)
      await hubCrmAPI.updateLead(id, { assigned_to_user_id: value })
      await loadLead()
    } catch (err) {
      alert(err.message || 'Failed to update assignee')
    } finally {
      setSaving(false)
    }
  }

  const handleAddNote = async (e) => {
    e.preventDefault()
    if (!noteText.trim()) return
    setSaving(true)
    try {
      await hubCrmAPI.addNote(id, noteText.trim())
      setNoteText('')
      await loadLead()
    } catch (err) {
      alert(err.message || 'Failed to add note')
    } finally {
      setSaving(false)
    }
  }

  const handleSpMigrate = async () => {
    if (
      !window.confirm(
        'Mark this case as migrated to Simple Practice? The case will be set to read-only status in CRM.'
      )
    ) {
      return
    }
    setSaving(true)
    try {
      await hubCrmAPI.markSpMigrated(id, spReference.trim())
      await loadLead()
    } catch (err) {
      alert(err.message || 'Failed to update')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <HubLayout appName="CRM">
        <p className="text-gray-600">Loading case...</p>
      </HubLayout>
    )
  }

  if (error || !data?.lead) {
    return (
      <HubLayout appName="CRM">
        <p className="text-red-800">{error || 'Case not found'}</p>
        <Link to="/hub/crm" className="text-green-700 text-sm mt-2 inline-block hover:underline">
          ← Back to CRM
        </Link>
      </HubLayout>
    )
  }

  const {
    lead,
    timeline,
    latest_intake,
    participants,
    contact_submissions: contactSubmissions,
    assignees,
    allowed_statuses,
    is_admin: isAdmin,
    can_nudge_lead: canNudgeLead,
    nudge_recipients: nudgeRecipients,
    can_call_lead: canCallLead,
    twilio_sms_configured: twilioSmsConfigured,
    crm_phone_enabled: crmPhoneEnabled,
    crm_sms_enabled: crmSmsEnabled,
  } = data
  const nudgeEligible = (nudgeRecipients || []).filter((r) => r.can_receive_nudge)
  const intakeFields = latest_intake
    ? flattenIntakeFields({
        client_type: latest_intake.client_type,
        service_type: latest_intake.service_type,
        form_data: latest_intake.form_data,
      })
    : []
  const readOnly = lead.status === 'in_simple_practice' || lead.status === 'archived'
  const clientEmail = resolveClientEmail(lead, participants)
  const clientPhone = resolveClientPhone(lead, participants)
  const canSendIntakeEmail = Boolean(clientEmail)
  const canSendIntakeSms =
    Boolean(twilioSmsConfigured && clientPhone && hasMarketingConsent(lead))
  const canSendIntakeLink = canSendIntakeEmail || canSendIntakeSms

  return (
    <HubLayout appName="CRM">
      <ConfirmDialog
        open={intakeLinkConfirmOpen}
        title="Send intake form link?"
        message="Send the website intake form link to the client. Case status may advance to Intake form sent. SMS requires marketing consent and may not deliver until A2P is approved."
        confirmText="Send"
        cancelText="Cancel"
        confirmVariant="success"
        onClose={() => setIntakeLinkConfirmOpen(false)}
        onConfirm={performSendIntakeLink}
      >
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Channels</p>
          <div className="flex flex-wrap gap-4">
            <label
              className={`flex items-center gap-2 text-sm ${!canSendIntakeEmail ? 'opacity-50' : ''}`}
            >
              <input
                type="checkbox"
                className="rounded border-gray-300 text-green-600 focus:ring-green-600"
                disabled={!canSendIntakeEmail}
                checked={intakeLinkChannels.includes('email')}
                onChange={(e) => toggleIntakeLinkChannel('email', e.target.checked)}
              />
              Email
            </label>
            <label
              className={`flex items-center gap-2 text-sm ${!canSendIntakeSms ? 'opacity-50' : ''}`}
            >
              <input
                type="checkbox"
                className="rounded border-gray-300 text-green-600 focus:ring-green-600"
                disabled={!canSendIntakeSms}
                checked={intakeLinkChannels.includes('sms')}
                onChange={(e) => toggleIntakeLinkChannel('sms', e.target.checked)}
              />
              SMS
            </label>
          </div>
          {canSendIntakeEmail ? (
            <div>
              <span className="text-gray-600">Email:</span>{' '}
              <span className="font-medium text-gray-900">{clientEmail}</span>
            </div>
          ) : (
            <p className="text-xs text-amber-700">No email on file for this case.</p>
          )}
          {canSendIntakeSms ? (
            <div>
              <span className="text-gray-600">SMS:</span>{' '}
              <span className="font-medium text-gray-900">{clientPhone}</span>
            </div>
          ) : clientPhone && !hasMarketingConsent(lead) ? (
            <p className="text-xs text-amber-700">
              SMS unavailable — case opted out of text messages.
            </p>
          ) : !crmSmsEnabled ? (
            <p className="text-xs text-amber-700">
              SMS unavailable — disabled in CRM settings (Twilio A2P pending).
            </p>
          ) : !twilioSmsConfigured ? (
            <p className="text-xs text-gray-500">SMS unavailable — Twilio not configured.</p>
          ) : (
            <p className="text-xs text-amber-700">No phone on file for this case.</p>
          )}
          <div>
            <label
              htmlFor="intake-link-language"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Message language
            </label>
            <select
              id="intake-link-language"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
              value={intakeLinkLang}
              onChange={(e) => setIntakeLinkLang(e.target.value)}
            >
              <option value="en">English</option>
              <option value="pt">Portuguese</option>
            </select>
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={nudgeConfirmOpen}
        title="Send friendly reminder?"
        message="Email a CRM team member a short reminder to follow up on this case. They will receive a link to open the case in VIVA HUB."
        confirmText="Send reminder"
        cancelText="Cancel"
        confirmVariant="primary"
        onClose={() => setNudgeConfirmOpen(false)}
        onConfirm={performSendNudge}
      >
        <div className="space-y-3">
          <div>
            <label
              htmlFor="nudge-recipient"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Send to
            </label>
            <select
              id="nudge-recipient"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
              value={nudgeRecipientId}
              onChange={(e) => setNudgeRecipientId(e.target.value)}
            >
              {(nudgeRecipients || []).map((r) => (
                <option key={r.id} value={r.id} disabled={!r.can_receive_nudge}>
                  {r.name}
                  {!r.can_receive_nudge ? ' (no email)' : ''}
                  {lead.assigned_to_user_id === r.id ? ' — Assignee' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="nudge-note" className="block text-xs font-medium text-gray-600 mb-1">
              Optional note (included in email)
            </label>
            <textarea
              id="nudge-note"
              rows={5}
              className="w-full min-h-[7.5rem] px-3 py-2 border border-gray-200 rounded-md text-sm resize-y"
              placeholder="e.g. Please call today — they asked about evening slots."
              value={nudgeNote}
              onChange={(e) => setNudgeNote(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={optOutConfirmOpen}
        title="Opt out of automated follow-ups?"
        message="This case will no longer receive automated intake reminders by email or text. You can still send messages manually from CRM."
        confirmText="Opt out"
        cancelText="Cancel"
        confirmVariant="danger"
        onClose={() => setOptOutConfirmOpen(false)}
        onConfirm={performOptOutFollowUp}
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Link to="/hub/crm" className="text-sm text-green-700 hover:underline">
          ← Back to cases
        </Link>
        <CrmHelpLink />
      </div>

      <header className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{lead.display_name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {LEAD_SOURCE_LABELS[lead.source] || lead.source}
              {lead.phone_raw && (
                <>
                  {' '}
                  ·{' '}
                  <a href={`tel:${lead.phone_raw}`} className="text-green-700">
                    {lead.phone_raw}
                  </a>
                </>
              )}
              {lead.email && (
                <>
                  {' '}
                  ·{' '}
                  <a href={`mailto:${lead.email}`} className="text-green-700">
                    {lead.email}
                  </a>
                </>
              )}
            </p>
          </div>
          <span
            className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${statusBadgeClass(lead.status)}`}
          >
            {LEAD_STATUS_LABELS[lead.status] || lead.status}
          </span>
        </div>

        {!readOnly && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select
                    disabled={saving}
                    className="px-3 py-1.5 rounded-md text-sm border border-gray-200 bg-white"
                    value={lead.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <option value={lead.status}>{LEAD_STATUS_LABELS[lead.status]}</option>
                    {allowed_statuses
                      .filter((s) => s !== lead.status)
                      .map((s) => (
                        <option key={s} value={s}>
                          {LEAD_STATUS_LABELS[s]}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Assignee</label>
                  <select
                    disabled={saving}
                    className="px-3 py-1.5 rounded-md text-sm border border-gray-200 bg-white min-w-[160px]"
                    value={lead.assigned_to_user_id ?? ''}
                    onChange={(e) => handleAssigneeChange(e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {(assignees || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                className="hidden sm:block w-px h-9 bg-gray-200 shrink-0 self-center"
                aria-hidden
              />
              <div className="border-t border-gray-200 sm:hidden" aria-hidden />

              <div className="flex flex-wrap items-end gap-3">
                <Button asChild variant="success" size="sm">
                  <Link to={`/hub/crm/${id}/intake`}>
                    {latest_intake ? 'Update intake form' : 'Intake form'}
                  </Link>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={saving || sendingIntakeLink || !canSendIntakeLink}
                  onClick={openIntakeLinkDialog}
                  title={
                    canSendIntakeLink
                      ? 'Send the intake form link by email and/or SMS'
                      : 'Add client email or phone (with marketing consent) first'
                  }
                >
                  {sendingIntakeLink ? 'Sending…' : 'Send intake form link'}
                </Button>
                {canCallLead ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving || callingLead || readOnly}
                    onClick={handleCallLead}
                    title="Ring your HUB profile phone, then connect to this case"
                  >
                    {callingLead ? 'Calling…' : 'Call client'}
                  </Button>
                ) : null}
                {canNudgeLead && nudgeEligible.length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving || sendingNudge}
                    onClick={openNudgeDialog}
                    title="Email a friendly reminder to the assignee or another CRM team member"
                  >
                    {sendingNudge ? 'Sending…' : 'Nudge team member'}
                  </Button>
                ) : null}
              </div>

              {lead.days_idle != null && (
                <p className="text-sm text-gray-600 sm:ml-auto">
                  Last activity: {formatDbDateTime(lead.last_activity_at)} ({lead.days_idle}d ago)
                </p>
              )}
            </div>
            {intakeLinkNotice ? (
              <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                {intakeLinkNotice}
              </p>
            ) : null}
            {nudgeNotice ? (
              <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                {nudgeNotice}
              </p>
            ) : null}
            {callNotice ? (
              <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                {callNotice}
              </p>
            ) : null}
            {canNudgeLead && !readOnly && nudgeEligible.length === 0 ? (
              <p className="text-xs text-amber-700">
                Nudge unavailable — no CRM assignee with an email address on file.
              </p>
            ) : null}

            {lead.marketing_consent === false ? (
              <p className="text-xs text-gray-600">
                Automated follow-ups are off for this case.{' '}
                <button
                  type="button"
                  className="text-green-700 underline"
                  disabled={saving}
                  onClick={() =>
                    hubCrmAPI
                      .updateLead(id, {
                        marketing_consent: true,
                        marketing_consent_note: 'Re-enabled in CRM',
                      })
                      .then(loadLead)
                      .catch((err) => alert(err.message))
                  }
                >
                  Re-enable
                </button>
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                Automated follow-ups allowed
                {lead.client_followup_count > 0
                  ? ` · ${lead.client_followup_count} sent`
                  : ''}
                {lead.marketing_consent_note ? ` · ${lead.marketing_consent_note}` : ''}
                {' · '}
                <button
                  type="button"
                  className="text-gray-600 underline hover:text-gray-900"
                  disabled={saving}
                  onClick={() => setOptOutConfirmOpen(true)}
                >
                  Opt out
                </button>
              </p>
            )}
          </div>
        )}

        {lead.status !== 'in_simple_practice' && !readOnly && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                SP reference (optional)
              </label>
              <input
                className="w-full px-3 py-1.5 rounded-md text-sm border border-gray-200"
                placeholder="Client ID or note in Simple Practice"
                value={spReference}
                onChange={(e) => setSpReference(e.target.value)}
              />
            </div>
            <Button type="button" variant="success" size="sm" disabled={saving} onClick={handleSpMigrate}>
              Migrated to Simple Practice
            </Button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Timeline</h2>
          {!readOnly && (
            <form onSubmit={handleAddNote} className="mb-4">
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm mb-2"
                placeholder="Add a note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <Button type="submit" variant="success" size="sm" disabled={saving}>
                Add note
              </Button>
            </form>
          )}
          <CrmLeadTimeline
            events={timeline || []}
            leadId={lead.id}
            leadDisplayName={lead.display_name}
          />
        </section>

        <div className="space-y-6">
          {contactSubmissions?.length > 0 && (
            <CollapsibleSection
              title="Contact form message"
              summary={
                contactSubmissions.length === 1
                  ? `Submitted ${formatDbDateTime(contactSubmissions[0].created_at)}`
                  : `${contactSubmissions.length} submissions`
              }
            >
              <div className="space-y-5">
                {contactSubmissions.map((submission) => {
                  const contactFields = flattenContactFields(submission).filter((field) =>
                    ['Subject', 'Message', 'Source', 'Language'].includes(field.label)
                  )
                  return (
                    <div
                      key={submission.id}
                      className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0"
                    >
                      {contactSubmissions.length > 1 ? (
                        <p className="text-xs text-gray-500 mb-3">
                          Submitted {formatDbDateTime(submission.created_at)}
                        </p>
                      ) : null}
                      <dl className="grid grid-cols-1 gap-x-6">
                        {contactFields.map((field) => (
                          <CopyableField
                            key={`${submission.id}-${field.label}`}
                            label={field.label}
                            value={field.value}
                          />
                        ))}
                      </dl>
                    </div>
                  )
                })}
              </div>
            </CollapsibleSection>
          )}

          {participants?.length > 0 && (
            <CollapsibleSection
              title="Client"
              summary={
                participants.length === 1
                  ? [participants[0].first_name, participants[0].last_name].filter(Boolean).join(' ') ||
                    '1 client'
                  : `${participants.length} clients`
              }
            >
              <div className="space-y-5">
                {participants.map((p) => {
                  const roleLabel =
                    PARTICIPANT_ROLE_LABELS[p.role] ||
                    p.role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                  const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ')
                  const phone = p.phone_raw || p.phone_normalized

                  return (
                    <div
                      key={p.id}
                      className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0"
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                        {roleLabel}
                      </p>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                        <CopyableField label="Name" value={fullName} />
                        <CopyableField label="Email" value={p.email} />
                        <CopyableField label="Phone" value={phone} />
                      </dl>
                    </div>
                  )
                })}
              </div>
            </CollapsibleSection>
          )}

          {!readOnly && !latest_intake && (
            <section className="bg-amber-50/80 rounded-lg border border-amber-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-1">No intake form on file</h2>
              <p className="text-sm text-gray-600 mb-3">
                If the client shared intake form details by phone or in person, enter them here so
                you can copy into Simple Practice later.
              </p>
              <Button asChild variant="success" size="sm">
                <Link to={`/hub/crm/${id}/intake`}>Intake form</Link>
              </Button>
            </section>
          )}

          {latest_intake && (
            <section className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                <h2 className="text-sm font-semibold text-gray-900">Latest intake form</h2>
                {!readOnly && (
                  <Link
                    to={`/hub/crm/${id}/intake`}
                    className="text-xs text-green-700 hover:underline"
                  >
                    Replace with new entry
                  </Link>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Saved {formatDbDateTime(latest_intake.created_at)} — copy into Simple Practice
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                {intakeFields.map((field) => (
                  <CopyableField key={field.label} label={field.label} value={field.value} />
                ))}
              </dl>
            </section>
          )}
        </div>
      </div>
    </HubLayout>
  )
}
